import type { SecurityFix, SecurityRow } from './types';

export type SecurityControlFilter = 'all' | SecurityRow['control'];

export const SECURITY_CONTROLS: Array<{ value: SecurityControlFilter; label: string }> = [
  { value: 'all', label: 'Tudo' },
  { value: 'login', label: 'Login' },
  { value: 'cadastro', label: 'Cadastro de usuário' },
  { value: 'escopo', label: 'Escopo (usuário, empresa, produto)' },
  { value: 'sem_login', label: 'Sem login' },
  { value: 'token', label: 'Token antigo' },
  { value: 'processo', label: 'Exceções engolidas' },
];

const REASONS: Record<string, string> = {
  'login:conta': 'Conta travada: 10 senhas erradas em 15 minutos.',
  'login:ip': 'IP travado: 50 senhas erradas em 5 minutos.',
  'login:senha_errada': 'A senha não confere.',
  'login:usuario_nao_existe': 'Nenhum usuário com esse e-mail.',
  'login:nao_validado': 'Usuário ainda não validado.',
  'login:credencial_invalida': 'Login sem e-mail e senha em texto no corpo da requisição.',
  'cadastro:negado': 'Cadastro ou edição de usuário recusado para quem chamou.',
  'cadastro:role': 'Cadastro pedindo um perfil que quem chamou não pode conceder.',
  'cadastro:reserved_email': 'Cadastro com o e-mail do master.',
  'cadastro:public_client_signup': 'Cadastro público tentando criar conta de empresa.',
  'escopo:usuario': 'Consulta de usuário fora das lojas de quem chamou.',
  'escopo:troca_de_senha': 'Troca de senha de usuário fora das lojas de quem chamou.',
  'escopo:cliente': 'Consulta de empresa fora do escopo de quem chamou.',
  'escopo:vincular_usuario': 'Vincular usuário a uma loja que quem chamou não administra.',
  'escopo:produto': 'Criar, editar ou apagar produto de loja que quem chamou não administra.',
  'escopo:so_admin': 'Rota que passou a ser só de Admin de plataforma.',
  'escopo:clientes_vazio': 'A lista de empresas voltou vazia para quem chamou (filtrada pelo escopo).',
  'sem_login:sem_login': 'Chamada sem token em rota que passou a exigir login.',
  'token:token_antigo': 'Token emitido antes de uma troca de senha. Precisa logar de novo.',
  'processo:excecao_nao_tratada': 'Exceção que antes derrubava a API e agora é engolida.',
  'processo:promise_rejeitada': 'Promise rejeitada sem tratamento (a API segue de pé).',
};

export function describeRow(row: SecurityRow): string {
  if (row.reason === 'credencial_na_query') {
    return row.status === 0
      ? 'Credencial na URL. Passou, mas ainda precisa migrar para o corpo da requisição.'
      : 'Credencial na URL recusada (a flag login_allow_query_credentials está desligada).';
  }
  return REASONS[`${row.control}:${row.reason}`] ?? `${row.control}: ${row.reason}`;
}

export function whoOf(row: SecurityRow): string {
  if (row.email) return row.email;
  if (row.actorId) return `Usuário ${row.actorId}`;
  return row.ip ? `IP ${row.ip}` : 'Sem identificação';
}

export function statusMeta(status: number): { label: string; variant: 'destructive' | 'warning' } {
  return status === 0 ? { label: 'Passou', variant: 'warning' } : { label: String(status), variant: 'destructive' };
}

export function fixLabel(fix: SecurityFix): string {
  if (fix.type === 'conta') return 'Liberar conta';
  if (fix.type === 'ip') return 'Liberar IP';
  return 'Liberar operador';
}

/** O que a liberação muda, dito sem rodeio: o botão abre a porta de novo para esse alvo. */
export function fixEffect(fix: SecurityFix): string {
  if (fix.type === 'conta') {
    return 'Essa conta deixa de travar por senha errada. O limite por IP continua valendo.';
  }
  if (fix.type === 'ip') {
    return 'Esse IP deixa de ter limite de tentativas de login e pode chamar POST /client e POST /product sem token, como antes do pentest.';
  }
  return 'Esse usuário volta a passar nas checagens novas de acesso: cadastro de usuário, consulta de usuários, empresas e produtos de qualquer loja, troca de senha e rotas só de Admin. Vale como Admin nelas.';
}

export function filterRows(rows: SecurityRow[], control: SecurityControlFilter, q: string): SecurityRow[] {
  const needle = q.trim().toLowerCase();
  return rows.filter((row) => {
    if (control !== 'all' && row.control !== control) return false;
    if (!needle) return true;
    return [row.email, row.ip, row.route, row.actorId, row.detail].some((field) =>
      field.toLowerCase().includes(needle),
    );
  });
}
